'use client';

import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel";
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { areaAPI, propertyAPI } from '@/lib/api';
import PropertyCard from './PropertyCard';

interface Property {
    id: string;
    title: string;
    location: string;
    price: number;
    rating: number;
    reviewCount: number;
    images: string[];
    isGuestFavourite: boolean;
}

interface AreaSection {
    areaId: string;
    areaLabel: string;
    properties: Property[];
    area_id: string;
    title: string;
}

// Dynamic content is fetched; static demo content removed.

interface AreaSliderProps {
    areaData: AreaSection;
}

function AreaSlider({ areaData }: AreaSliderProps) {
    return (
        <div className="mb-10">
            <div className="mb-6 flex items-center justify-between gap-2">
                <h3 className="text-2xl font-medium text-gray-900 w-fit">
                    <span className="relative">{areaData.areaLabel}
                        <div className="absolute -bottom-0.5 right-0 w-20 h-1 bg-primary" />
                    </span>
                </h3>
                <Link href={`/listings?area=${areaData.areaId}`} className="font-medium text-gray-900 w-fit underline">
                    View All
                </Link>
            </div>
            <div className="relative">
                <Carousel
                    opts={{
                        align: "start",
                        loop: true,
                    }}
                    className="w-full"
                >
                    <CarouselContent className="-ml-2 md:-ml-4">
                        {(Array.isArray(areaData?.properties) ? areaData.properties : []).map((property: any, index: number) => (
                            <CarouselItem
                                key={property?.id || property?._id || `property-${index}`}
                                className="pl-2 md:pl-4 basis-11/12 md:basis-1/2 lg:basis-1/3 xl:basis-1/4 2xl:basis-1/5 py-2"
                            >
                                <PropertyCard property={property} />
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                    <CarouselPrevious className="absolute top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-lg border border-white/70 w-10 h-10 rounded-full" />
                    <CarouselNext className="absolute top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-lg border border-white/70 w-10 h-10 rounded-full" />
                </Carousel>
            </div>
        </div>
    );
}

export default function FeaturedProperties() {
    const [sections, setSections] = useState<AreaSection[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchAreasAndProperties = async () => {
        try {
            const resp = await areaAPI.getFrontendList({ limit: 5 });
            const raw = (resp as any)?.data;
            const areas = (raw?.data ?? raw?.items ?? raw?.results ?? raw) as any[];
            const validAreas = Array.isArray(areas) ? areas.filter((a) => a && (a.id || a._id) && (a.name || a.title)) : [];

            const allPropsResp = await propertyAPI.getAllWeb({ limit: 10 });
            const allPropsRaw = (allPropsResp as any)?.data;

            const allPropsSection: AreaSection = {
                areaId: '',
                areaLabel: 'Featured Properties',
                title: 'Featured Properties',
                properties: allPropsRaw,
                area_id: ''
            };

            const results = await Promise.all(
                validAreas.map(async (area: any) => {
                    try {
                        const area_id = area._id
                        const areaId = area.externalId;
                        const areaLabel = area.name || area.title || 'Area';
                        const propResp = await propertyAPI.getAllWeb({ area: area.externalId, limit: 10 });
                        const propRaw = (propResp as any)?.data;

                        return {
                            areaId,
                            areaLabel: `Popular homes in ${areaLabel}`,
                            title: areaLabel,
                            properties: propRaw,
                            area_id: area_id || areaId
                        } as AreaSection;
                    } catch (e) {
                        return null;
                    }
                })
            );

            const areaSections = results.filter(Boolean) as AreaSection[];
            setSections([allPropsSection, ...areaSections]);
        } catch (error) {
            console.error('Error fetching areas or properties:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAreasAndProperties();
    }, []);

    // Skeleton loading component
    if (loading) {
        return (
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    {[1, 2, 3].map((sectionIndex) => (
                        <div key={sectionIndex} className="mb-10">
                            <div className="mb-6 flex items-center justify-between gap-2">
                                <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
                                <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                            </div>
                            <div className="flex gap-4 overflow-hidden">
                                {[1, 2, 3, 4].map((cardIndex) => (
                                    <div key={cardIndex} className="min-w-[300px] md:min-w-[350px] flex-shrink-0">
                                        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                                            <div className="h-48 bg-gray-200 animate-pulse" />
                                            <div className="p-4 space-y-3">
                                                <div className="h-6 w-3/4 bg-gray-200 rounded animate-pulse" />
                                                <div className="h-4 w-1/2 bg-gray-200 rounded animate-pulse" />
                                                <div className="flex justify-between mt-4">
                                                    <div className="h-4 w-1/4 bg-gray-200 rounded animate-pulse" />
                                                    <div className="h-4 w-1/4 bg-gray-200 rounded animate-pulse" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    return (
        <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-4">
                {/* <div className="text-center mb-16">
                    <h2 className="text-3xl lg:text-4xl font-semibold text-gray-900 mb-6">
                        <span className='text-primary'>Featured Properties</span> by Area
                    </h2>
                    <p className="text-lg text-gray-600">
                        Discover the best accommodations in Dubai&apos;s most sought-after locations
                    </p>
                </div> */}

                {sections?.map((section, idx) => (
                    <AreaSlider key={section.areaId || section.area_id || idx} areaData={section} />
                ))}
            </div>
        </section>
    );
}
